import type { Location } from 'react-router-dom'

export interface ReturnPathState {
  returnTo: string
}

const DEFAULT_RETURN_PATH = '/'
const PASSWORD_CHANGE_PATH = '/profile'
const PATH_ORIGIN_PROBE = 'http://return-path.invalid'
const AUTH_ENTRY_PATHS = new Set(['/login', '/reset-password'])
const UNSAFE_CHARACTERS = /[\s\\]/

function decodePathname(pathname: string): string {
  try {
    return decodeURIComponent(pathname)
  } catch {
    return pathname
  }
}

function normalizeRoutePath(pathname: string): string {
  const withoutTrailingSlashes = decodePathname(pathname).replace(/\/+$/, '')
  return (withoutTrailingSlashes || '/').toLowerCase()
}

function toSafeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/') || value.startsWith('//')) return null
  if (UNSAFE_CHARACTERS.test(value)) return null

  let parsed: URL
  try {
    parsed = new URL(value, PATH_ORIGIN_PROBE)
  } catch {
    return null
  }

  if (parsed.origin !== PATH_ORIGIN_PROBE) return null
  if (AUTH_ENTRY_PATHS.has(normalizeRoutePath(parsed.pathname))) return null

  return `${parsed.pathname}${parsed.search}`
}

function readReturnTo(state: unknown): unknown {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return undefined
  return (state as Partial<Record<keyof ReturnPathState, unknown>>).returnTo
}

export function resolveReturnPath(state: unknown): string {
  return toSafeReturnPath(readReturnTo(state)) ?? DEFAULT_RETURN_PATH
}

export function resolvePostLoginPath(state: unknown, requirePasswordChange: boolean): string {
  return requirePasswordChange ? PASSWORD_CHANGE_PATH : resolveReturnPath(state)
}

export function buildReturnPathState(location: Pick<Location, 'pathname' | 'search' | 'state'>): ReturnPathState | undefined {
  const returnTo = toSafeReturnPath(`${location.pathname}${location.search}`)
    ?? toSafeReturnPath(readReturnTo(location.state))

  return returnTo ? { returnTo } : undefined
}
